import dayjs from 'dayjs';
import { MHColours } from '../../constants/theme';
import {
  ICalendarEvent,
  IChartDot,
  IDailyStatistics,
  IDateRangeStatistics,
  IDiaryEntry,
  MigrainesEnum,
  MigrainesShortEnum,
  YesNoAnswer,
  YesNoAnswerSwedish,
} from '../interfaces';
import i18n from '../../i18next';

export const loadEvents = (diaryEntries: IDiaryEntry[]): ICalendarEvent[] => {
  const calendarEvents: ICalendarEvent[] = [];
  let eventId = 0;
  if (diaryEntries && diaryEntries.length) {
    diaryEntries.forEach((diaryEntry) => {
      const migraineEvents = getMigraineEvents(diaryEntry);
      const title = migraineEvents.textInputs
        .join('')
        .replace(/\bhorton\b/gi, i18n.t('horton'));

      calendarEvents.push({
        id: eventId,
        title,
        start: new Date(diaryEntry.diaryDate),
        end: new Date(diaryEntry.diaryDate),
        entries: migraineEvents.migraines,
      });
      eventId += 1;
    });
  }
  return calendarEvents;
};

export const getMigraineEvents = (
  diaryEntrie: IDiaryEntry
): { migraines: string[]; textInputs: string[] } => {
  const migraines: string[] = [];
  const textInputs: string[] = [];

  diaryEntrie?.answers.forEach((answer) => {
    switch (true) {
      case answer.order === 1:
        if (
          answer.answer[0].value === YesNoAnswer.No ||
          answer.answer[0].value === YesNoAnswerSwedish.No
        ) {
          migraines.push(MigrainesShortEnum.IngenHuvuduvark);
        } else {
          migraines.push(MigrainesShortEnum.Huvuduvark);
        }
        textInputs.push(`${answer.answer[0].text} \n`);
        break;
      case answer.order === 2 &&
        (answer.answer[0].value === YesNoAnswer.Yes ||
          answer.answer[0].value === YesNoAnswerSwedish.Yes):
        migraines.push(MigrainesShortEnum.AuraUtanHuvuduvark);
        textInputs.push(`${answer.answer[0].text} \n`);
        break;
      case answer.order === 3:
        answer.answer.forEach((migraineAnswer) => {
          if (migraineAnswer.value === MigrainesEnum.Horton) {
            migraines.push(MigrainesShortEnum.Horton);
          } else if (
            migraineAnswer.value === MigrainesEnum.Migraine ||
            migraineAnswer.value === 'Migraine'
          ) {
            migraines.push(MigrainesShortEnum.Migraine);
          } else if (
            migraineAnswer.value === MigrainesEnum.Tension ||
            migraineAnswer.value === 'Tension headache'
          ) {
            migraines.push(MigrainesShortEnum.Tension);
          } else {
            migraines.push(MigrainesShortEnum.AnnanHuvuduvark);
          }
        });
        break;
      case answer.order === 5:
        textInputs.push(
          `${i18n.t('vasMigraine')}: ${answer.answer[0].value} \n`
        );
        break;
      case answer.order === 7:
        textInputs.push(`${i18n.t('vasHorton')}: ${answer.answer[0].value} \n`);
        break;
      case answer.order === 9:
        textInputs.push(
          `${i18n.t('vasTensionHeadache')}: ${answer.answer[0].value} \n`
        );
        break;
      case answer.order === 11:
        textInputs.push(
          `${i18n.t('vasOtherHeadache')}: ${answer.answer[0].value} \n`
        );
        break;
      case answer.order === 12 &&
        (answer.answer[0].value === YesNoAnswer.Yes ||
          answer.answer[0].value === YesNoAnswerSwedish.Yes):
        migraines.push(MigrainesShortEnum.Aura);
        textInputs.push(`${answer.answer[0].text} \n`);
        break;
      case answer.order === 13 &&
        (answer.answer[0].value === YesNoAnswer.Yes ||
          answer.answer[0].value === YesNoAnswerSwedish.Yes):
        migraines.push(MigrainesShortEnum.Mens);
        textInputs.push(`${answer.answer[0].text} \n`);
        break;
      case answer.order === 14 &&
        (answer.answer[0].value === YesNoAnswer.Yes ||
          answer.answer[0].value === YesNoAnswerSwedish.Yes):
        migraines.push(MigrainesShortEnum.Lakemedel);
        textInputs.push(`${answer.answer[0].text} \n`);
        break;
      case answer.order === 15 && answer.answer[0].value !== undefined:
        textInputs.push(`${answer.answer[0].value} \n`);
        break;

      default:
        if (answer.answer[0]?.text) {
          textInputs.push(`${answer.answer[0].text} \n`);
        }
    }
  });
  return { migraines, textInputs };
};

export const mapColorToEntrie = (entrie: string) => {
  switch (entrie) {
    case MigrainesShortEnum.Aura:
      return MHColours.mhMigrainePink;
    case MigrainesShortEnum.Huvuduvark:
      return MHColours.mhMigraineRed;
    case MigrainesShortEnum.Migraine:
      return MHColours.mhMigraineBlue;
    case MigrainesShortEnum.Horton:
      return MHColours.mhMigraineYellow;
    case MigrainesShortEnum.Tension:
      return MHColours.mhMigrainePurpleLight;
    case MigrainesShortEnum.AnnanHuvuduvark:
      return MHColours.mhMigraineOrange;
    case MigrainesShortEnum.AuraUtanHuvuduvark:
      return MHColours.mhMigrainePurple;
    case MigrainesShortEnum.IngenHuvuduvark:
      return MHColours.mhMigraineGreenLight;
    case MigrainesShortEnum.Lakemedel:
      return MHColours.mhMigraineGreenFluorescent;
    case MigrainesShortEnum.Mens:
      return MHColours.mhMigraineSeeBlue;
    default:
      return 'Unknown';
  }
};

export const mapTextToEntrie = (entrie: string) => {
  switch (entrie) {
    case MigrainesShortEnum.Aura:
      return i18n.t('aura');
    case MigrainesShortEnum.Huvuduvark:
      return i18n.t('headache');
    case MigrainesShortEnum.Migraine:
      return i18n.t('migrain');
    case MigrainesShortEnum.Horton:
      return i18n.t('horton');
    case MigrainesShortEnum.Tension:
      return i18n.t('tensionHeadache');
    case MigrainesShortEnum.AnnanHuvuduvark:
      return i18n.t('otherHeadache');
    case MigrainesShortEnum.AuraUtanHuvuduvark:
      return i18n.t('auraWithoutHeadache');
    case MigrainesShortEnum.IngenHuvuduvark:
      return i18n.t('noHeadache');
    case MigrainesShortEnum.Lakemedel:
      return i18n.t('medication');
    case MigrainesShortEnum.Mens:
      return i18n.t('menstruation');
    default:
      return 'Unknown';
  }
};

export const areDatesEqualWithoutTime = (date1: string, date2: string) => {
  const formattedDate1 = dayjs(date1).format('YYYY-MM-DD');
  const formattedDate2 = dayjs(date2).format('YYYY-MM-DD');

  return formattedDate1 === formattedDate2;
};

export const getStatistics = (
  statisticEntries: IDiaryEntry[]
): IDateRangeStatistics => {
  const statistics: IDateRangeStatistics = {
    numberOfDays: 0,
    numberOfDaysWithHeadaches: 0,
    numberOfMigraines: 0,
    numberOfTensionHeadaches: 0,
    numberOfHortons: 0,
    numberOfOtherHeadaches: 0,
    numberOfAuras: 0,
    numberOfAurasWithoutHeadaches: 0,
    numberOfWithoutHeadaches: 0,
    numberOfLakemedels: 0,
    numberOfMens: 0,

    totalSumOfIntensitiesHeadaches: 0,
    totalSumOfIntensitiesTensionHeadaches: 0,
    totalSumOfIntensitiesMigraines: 0,
    totalSumOfIntensitiesHortons: 0,
    totalSumOfIntensitiesOtherHeadaches: 0,
  };

  if (statisticEntries && statisticEntries.length) {
    statisticEntries.forEach((statisticEntry) => {
      statisticEntry?.answers.forEach((answer) => {
        switch (true) {
          case answer.order === 1:
            if (
              answer.answer[0].value === YesNoAnswer.No ||
              answer.answer[0].value === YesNoAnswerSwedish.No
            ) {
              statistics.numberOfWithoutHeadaches += 1;
            } else {
              statistics.numberOfDaysWithHeadaches += 1;
            }
            break;
          // api-service asks about aura twice and only one is asked per day:
          // order 2 on days without headache, order 12 on days with headache.
          case answer.order === 2 &&
            (answer.answer[0].value === YesNoAnswer.Yes ||
              answer.answer[0].value === YesNoAnswerSwedish.Yes):
            statistics.numberOfAurasWithoutHeadaches += 1;
            break;
          case answer.order === 12 &&
            (answer.answer[0].value === YesNoAnswer.Yes ||
              answer.answer[0].value === YesNoAnswerSwedish.Yes):
            statistics.numberOfAuras += 1;
            break;
          case answer.order === 3:
            answer.answer.forEach((migraineAnswer) => {
              if (migraineAnswer.value === MigrainesEnum.Horton) {
                statistics.numberOfHortons += 1;
              } else if (
                migraineAnswer.value === MigrainesEnum.Migraine ||
                migraineAnswer.value === 'Migraine'
              ) {
                statistics.numberOfMigraines += 1;
              } else if (
                migraineAnswer.value === MigrainesEnum.Tension ||
                migraineAnswer.value === 'Tension headache'
              ) {
                statistics.numberOfTensionHeadaches += 1;
              } else {
                statistics.numberOfOtherHeadaches += 1;
              }
            });
            break;
          case answer.order === 5:
            statistics.totalSumOfIntensitiesMigraines += +(
              answer.answer[0].value || 0
            );
            statistics.totalSumOfIntensitiesHeadaches += +(
              answer.answer[0].value || 0
            );
            break;
          case answer.order === 7:
            statistics.totalSumOfIntensitiesHortons += +(
              answer.answer[0].value || 0
            );
            statistics.totalSumOfIntensitiesHeadaches += +(
              answer.answer[0].value || 0
            );
            break;
          case answer.order === 9:
            statistics.totalSumOfIntensitiesTensionHeadaches += +(
              answer.answer[0].value || 0
            );
            statistics.totalSumOfIntensitiesHeadaches += +(
              answer.answer[0].value || 0
            );
            break;
          case answer.order === 11:
            statistics.totalSumOfIntensitiesOtherHeadaches += +(
              answer.answer[0].value || 0
            );
            statistics.totalSumOfIntensitiesHeadaches += +(
              answer.answer[0].value || 0
            );
            break;
          case answer.order === 13 &&
            (answer.answer[0].value === YesNoAnswer.Yes ||
              answer.answer[0].value === YesNoAnswerSwedish.Yes):
            statistics.numberOfMens += 1;
            break;
          case answer.order === 14 &&
            (answer.answer[0].value === YesNoAnswer.Yes ||
              answer.answer[0].value === YesNoAnswerSwedish.Yes):
            statistics.numberOfLakemedels += 1;
            break;
          default:
            break;
        }
      });
    });
    statistics.numberOfDays = statisticEntries.length;
  }

  return statistics;
};

export const splitDiaryEntries = (
  diaryEntries: IDiaryEntry[],
  weekly: boolean = false
): Record<string, IDiaryEntry[]> => {
  const result: Record<string, IDiaryEntry[]> = {};

  diaryEntries.forEach((obj) => {
    const diaryDate = new Date(obj.diaryDate);
    let key;

    if (weekly) {
      const firstDayOfWeek = new Date(diaryDate);
      firstDayOfWeek.setDate(diaryDate.getDate() - diaryDate.getDay());
      key = `${firstDayOfWeek.getFullYear()}-${firstDayOfWeek.getMonth() + 1}-${firstDayOfWeek.getDate()}`;
    } else {
      key = `${diaryDate.getFullYear()}-${diaryDate.getMonth() + 1}`;
    }

    if (!result[key]) {
      result[key] = [];
    }

    result[key].push(obj);
  });

  return result;
};

export const sortDiaryEntriesByDate = (array: IDiaryEntry[]): IDiaryEntry[] => {
  const arrayCopy = [...array];
  return arrayCopy.sort(
    (a, b) => new Date(a.diaryDate).getTime() - new Date(b.diaryDate).getTime()
  );
};

export const sortByDateAndAddMissingDays = (
  array: IChartDot[],
  startDate: string,
  endDate: string
): IChartDot[] => {
  const sortedData = array;
  const result: IChartDot[] = [];

  if (!sortedData.length) {
    return result;
  }

  let currentDate = new Date(startDate);
  while (currentDate.toISOString().split('T')[0] < sortedData[0].x) {
    const missingDayObject: IChartDot = {
      x: currentDate.toISOString().split('T')[0],
      y: undefined,
    };
    result.push(missingDayObject);
    currentDate.setDate(currentDate.getDate() + 1);
  }

  for (let i = 0; i < sortedData.length - 1; i++) {
    result.push(sortedData[i]);

    const currentObjDate = new Date(sortedData[i].x);
    const nextObjDate = new Date(sortedData[i + 1].x);

    while (
      currentObjDate.getTime() <
      nextObjDate.getTime() - 24 * 60 * 60 * 1000
    ) {
      currentObjDate.setDate(currentObjDate.getDate() + 1);

      const missingDayObject: IChartDot = {
        x: currentObjDate.toISOString().split('T')[0],
        y: undefined,
      };

      result.push(missingDayObject);
    }
  }

  result.push(sortedData[sortedData.length - 1]);

  currentDate = new Date(sortedData[sortedData.length - 1].x);

  const lastDate = new Date(endDate);

  while (
    currentDate.toISOString().split('T')[0] <
    lastDate.toISOString().split('T')[0]
  ) {
    currentDate.setDate(currentDate.getDate() + 1);

    const missingDayObject: IChartDot = {
      x: currentDate.toISOString().split('T')[0],
      y: undefined,
    };

    result.push(missingDayObject);
  }

  return result;
};

export const getStatisticsByMonthsOrWeeks = (
  statisticEntries: IDiaryEntry[],
  weekly: boolean = false
): Record<string, IDateRangeStatistics> => {
  const result: Record<string, IDateRangeStatistics> = {};
  if (statisticEntries && statisticEntries.length) {
    const entriesByMonths = splitDiaryEntries(statisticEntries, weekly);
    const months = Object.keys(entriesByMonths).sort(
      (a: string, b: string) => new Date(a).getTime() - new Date(b).getTime()
    );

    if (months.length) {
      months.forEach((month) => {
        result[month] = getStatistics(entriesByMonths[month]);
      });
    }
  }
  return result;
};

const getChartDotObject = (x: string, y: number) => ({
  x: dayjs(x).format('YYYY-MM-DD'),
  y,
});

export const getStatisticsByDays = (
  statisticEntries: IDiaryEntry[],
  startDate: string,
  endDate: string
): IDailyStatistics => {
  const statistics: IDailyStatistics = {
    headaches: [],
    tesnionHeadaches: [],
    migraines: [],
    hortons: [],
    otherHeadaches: [],
  };

  if (statisticEntries && statisticEntries.length) {
    statisticEntries.forEach((statisticEntry) => {
      let headacheIntensity = 0;
      let tensionIntensity = 0;
      let migraineIntensity = 0;
      let hortonIntensity = 0;
      let otherHeadachesIntensity = 0;
      let migraineAnswered = false;
      let hortonAnswered = false;
      let tensionAnswered = false;
      let otherAnswered = false;

      if (
        statisticEntry.answers[0].order === 1 &&
        (statisticEntry.answers[0].answer[0].value === YesNoAnswer.No ||
          statisticEntry.answers[0].answer[0].value === YesNoAnswerSwedish.No)
      ) {
        return;
      }

      statisticEntry?.answers.forEach((answer) => {
        switch (true) {
          case answer.order === 5:
            migraineIntensity = +(answer.answer[0].value || 0);
            headacheIntensity += migraineIntensity;
            migraineAnswered = true;
            break;
          case answer.order === 7:
            hortonIntensity = +(answer.answer[0].value || 0);
            headacheIntensity += hortonIntensity;
            hortonAnswered = true;
            break;
          case answer.order === 9:
            tensionIntensity = +(answer.answer[0].value || 0);
            headacheIntensity += tensionIntensity;
            tensionAnswered = true;
            break;
          case answer.order === 11:
            otherHeadachesIntensity = +(answer.answer[0].value || 0);
            headacheIntensity += otherHeadachesIntensity;
            otherAnswered = true;
            break;
          default:
            break;
        }
      });

      const { diaryDate } = statisticEntry;

      const answeredCount = [
        migraineAnswered,
        hortonAnswered,
        tensionAnswered,
        otherAnswered,
      ].filter(Boolean).length;
      if (answeredCount > 0) {
        statistics.headaches.push(
          getChartDotObject(diaryDate, headacheIntensity / answeredCount)
        );
      }
      if (tensionAnswered) {
        statistics.tesnionHeadaches.push(
          getChartDotObject(diaryDate, tensionIntensity)
        );
      }
      if (migraineAnswered) {
        statistics.migraines.push(
          getChartDotObject(diaryDate, migraineIntensity)
        );
      }
      if (hortonAnswered) {
        statistics.hortons.push(getChartDotObject(diaryDate, hortonIntensity));
      }
      if (otherAnswered) {
        statistics.otherHeadaches.push(
          getChartDotObject(diaryDate, otherHeadachesIntensity)
        );
      }
    });
  }

  return {
    headaches: sortByDateAndAddMissingDays(
      statistics.headaches,
      startDate,
      endDate
    ),
    tesnionHeadaches: sortByDateAndAddMissingDays(
      statistics.tesnionHeadaches,
      startDate,
      endDate
    ),
    migraines: sortByDateAndAddMissingDays(
      statistics.migraines,
      startDate,
      endDate
    ),
    hortons: sortByDateAndAddMissingDays(
      statistics.hortons,
      startDate,
      endDate
    ),
    otherHeadaches: sortByDateAndAddMissingDays(
      statistics.otherHeadaches,
      startDate,
      endDate
    ),
  };
};

export const calculateAverageHeadache = (
  sumOfHeadaches: number,
  numberOfHeadaches: number
): number => {
  if (sumOfHeadaches === 0 || numberOfHeadaches === 0) return 0;
  return +(sumOfHeadaches / numberOfHeadaches).toFixed(2);
};

export const getMonthName = (yearMonth: string, swedish = true): string => {
  let months;
  if (swedish) {
    months = [
      'Januari',
      'Februari',
      'Mars',
      'April',
      'Maj',
      'Juni',
      'Juli',
      'Augusti',
      'September',
      'Oktober',
      'November',
      'December',
    ];
  } else {
    months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
  }
  return months[+yearMonth.split('-')[1] - 1];
};

export const getChartsStatisticsByMonths = (
  statisticByMonths: Record<string, IDateRangeStatistics>
): { statistics: IDailyStatistics; labels: string[] } => {
  const statistics: IDailyStatistics = {
    headaches: [],
    tesnionHeadaches: [],
    migraines: [],
    hortons: [],
    otherHeadaches: [],
  };
  const labels: string[] = [];

  let firstMonth = -1;

  Object.entries(statisticByMonths).forEach(([key, month]) => {
    if (firstMonth === -1) {
      firstMonth = dayjs(key).month() - 1;
    }
    statistics.headaches.push({
      x: getMonthName(key),
      y: month.numberOfDaysWithHeadaches || 0,
    });
    statistics.migraines.push({
      x: getMonthName(key),
      y: month.numberOfMigraines || 0,
    });
    statistics.tesnionHeadaches.push({
      x: getMonthName(key),
      y: month.numberOfTensionHeadaches || 0,
    });
    statistics.hortons.push({
      x: getMonthName(key),
      y: month.numberOfHortons || 0,
    });
    statistics.otherHeadaches.push({
      x: getMonthName(key),
      y: month.numberOfOtherHeadaches || 0,
    });
    labels.push(getMonthName(key));
  });

  return { statistics, labels };
};
